import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';
import { TABLE_ZOOM, centreOffset, claimsSideways, framingCamera, keepInView, liftOffset, maxPan, minTableHeight, pageCamera, tableHeight } from './tableFit';
import type { Bounds, Origin, Span } from './tableFit';
import { useCamera } from './useCamera';

const noop = () => {};
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

/** A step that would pass over 100% lands on it, so the buttons always reach it. */
export function steppedZoom(from: number, factor: number): number {
  const to = from * factor;
  if ((from < 1 && to > 1) || (from > 1 && to < 1) || Math.abs(to - 1) < 0.015) return 1;
  return to;
}

/** How a canvas was last left in this browser: the page's zoom and sideways reach, and where full
 *  canvas was looking. */
interface TableView {
  zoom: number;
  pan: number;
  free?: { x: number; y: number; zoom: number };
}

const VIEWS_KEY = 'table.views';

function readViews(): Record<string, TableView> {
  try {
    return JSON.parse(localStorage.getItem(VIEWS_KEY) ?? '{}') as Record<string, TableView>;
  } catch {
    return {};
  }
}

function keepView(key: string, view: TableView) {
  try {
    localStorage.setItem(VIEWS_KEY, JSON.stringify({ ...readViews(), [key]: view }));
  } catch {
    // Not kept; the canvas opens as it would have anyway.
  }
}

/**
 * The canvas as a table on a page: a bounded surface as tall as what is placed, at the zoom it
 * opens at, until full canvas hands it a camera of its own.
 *
 * In the page state the camera is derived from what is placed, so a stored position is never
 * touched and a vertical wheel is left for the page to scroll. The returned [cam] has the
 * camera's shape, with panning and the wheel's pan turned off there; full canvas is the plain
 * camera. [slot] goes on the box that holds the table in the page: its width and top are what the
 * table's height rule reads.
 *
 * What the table is fitted to — the top-left of what is placed, and how much it takes up — is
 * latched once [ready] and held while [pageKey] stays: moving the card that is top-left-most, or
 * letting one go beyond it, moves only that card. Where what is placed is smaller than the table it
 * is centred in it, by that latched size.
 */
export function useTable(bounds: Bounds | null, pageKey: string, ready = true) {
  const free = useCamera({ x: 0, y: 0, zoom: 1 }, TABLE_ZOOM);
  const [full, setFullState] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState(0);
  const slot = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ width: 0, top: 0 });
  const [, resized] = useReducer((n: number) => n + 1, 0);
  /** Drawn again once the table's box has stopped moving, for whatever is placed against it. */
  const [, settle] = useReducer((n: number) => n + 1, 0);

  useLayoutEffect(() => {
    window.addEventListener('resize', resized);
    return () => window.removeEventListener('resize', resized);
  }, []);
  // The table's own width moves without the window's — a tray sliding in or out beside it — and
  // what is centred on it has to follow every frame of that, not catch up once it stops.
  const watched = useRef<{ el: Element; watch: ResizeObserver } | null>(null);
  useLayoutEffect(() => {
    const el = slot.current;
    if (watched.current?.el === el || typeof ResizeObserver === 'undefined') return;
    watched.current?.watch.disconnect();
    watched.current = null;
    if (!el) return;
    const watch = new ResizeObserver(resized);
    watch.observe(el);
    watched.current = { el, watch };
  });
  useEffect(() => () => watched.current?.watch.disconnect(), []);
  useLayoutEffect(() => {
    const el = slot.current;
    if (!el) return;
    const page = el.offsetParent?.getBoundingClientRect().top ?? 0;
    const next = { width: el.offsetWidth, top: page + el.offsetTop };
    setFrame((was) => (was.width === next.width && was.top === next.top ? was : next));
  });

  const held = useRef<{ key: string; origin: Origin; span: Span | null } | null>(null);
  if (held.current?.key !== pageKey) held.current = null;
  if (!held.current && ready) {
    held.current = {
      key: pageKey,
      origin: bounds ? { x: bounds.minX, y: bounds.minY } : { x: 0, y: 0 },
      span: bounds ? { w: bounds.maxX - bounds.minX, h: bounds.maxY - bounds.minY } : null,
    };
  }
  const origin = held.current?.origin ?? (bounds ? { x: bounds.minX, y: bounds.minY } : null);
  const span = held.current?.span ?? null;
  const originKey = origin && `${origin.x},${origin.y}`;

  const min = minTableHeight(window.innerHeight, frame.top);
  const lift = liftOffset(span, zoom, min);
  const centre = centreOffset(span, zoom, frame.width);
  const height = tableHeight(bounds, origin, zoom, min, lift);
  const reach = maxPan(bounds, origin, zoom, frame.width);
  const panned = clamp(pan, 0, reach);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- [origin] is tracked by its key
  const page = useMemo(() => pageCamera(origin, zoom, panned, centre, lift), [originKey, zoom, panned, centre, lift]);

  const mainSize = () => {
    const main = document.querySelector('.nook-main')?.getBoundingClientRect();
    return { w: main?.width ?? window.innerWidth, h: main?.height ?? window.innerHeight };
  };

  const setFull = useCallback(
    (on: boolean) => {
      if (on) free.setCamera(readViews()[pageKey]?.free ?? framingCamera(bounds, mainSize(), TABLE_ZOOM));
      setFullState(on);
    },
    [free, bounds, pageKey],
  );

  // Each canvas opens as it was last left here.
  useLayoutEffect(() => {
    const view = readViews()[pageKey];
    setZoom(view?.zoom ?? 1);
    setPan(view?.pan ?? 0);
  }, [pageKey]);
  const freeCamera = free.camera;
  useEffect(() => {
    const id = setTimeout(() => {
      const was = readViews()[pageKey];
      keepView(pageKey, { zoom, pan: panned, free: full ? freeCamera : was?.free });
    }, 300);
    return () => clearTimeout(id);
  }, [pageKey, zoom, panned, full, freeCamera]);

  /** Back to 100%: the page's zoom from the left of what is placed, or full canvas centred on it. */
  const reset = useCallback(() => {
    if (full) free.setCamera(framingCamera(bounds, mainSize(), { min: 1, max: 1 }));
    else {
      setZoom(1);
      setPan(0);
    }
  }, [full, free, bounds]);

  const onWheel = useCallback(
    (e: ReactWheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setZoom((z) => clamp(z * (1 - e.deltaY / 400), TABLE_ZOOM.min, TABLE_ZOOM.max));
      } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        setPan(clamp(panned + e.deltaX, 0, reach));
      }
    },
    [panned, reach],
  );

  /** A drag on the page's table: sideways it reaches along the table, up and down it scrolls the
   *  page the table sits in. */
  const dragging = useRef<{ x: number; y: number } | null>(null);
  const beginDrag = useCallback((event: ReactPointerEvent) => {
    dragging.current = { x: event.clientX, y: event.clientY };
  }, []);
  const dragMove = useCallback(
    (event: ReactPointerEvent) => {
      const from = dragging.current;
      if (!from) return;
      dragging.current = { x: event.clientX, y: event.clientY };
      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      if (dx !== 0) setPan((p) => clamp(p - dx, 0, reach));
      if (dy !== 0) slot.current?.closest('.table-scroll')?.scrollBy?.({ top: -dy });
    },
    [reach],
  );
  const endDrag = useCallback(() => {
    dragging.current = null;
  }, []);

  const cam = useMemo(
    () =>
      full
        ? free
        : {
            ...free,
            camera: page,
            onWheel,
            beginPan: beginDrag,
            onPointerMove: dragMove,
            endPan: endDrag,
            wantsPan: () => false,
            isPanning: () => dragging.current !== null,
            zoomBy: noop,
            zoomAt: noop,
            toCanvas: (sx: number, sy: number, rect: DOMRect) => ({
              x: (sx - rect.left - page.x) / page.zoom,
              y: (sy - rect.top - page.y) / page.zoom,
            }),
          },
    [full, free, page, onWheel, beginDrag, dragMove, endDrag],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const box = slot.current?.querySelector('.table')?.getBoundingClientRect();
      const now = free.camera.zoom;
      const landed = clamp(steppedZoom(now, factor), TABLE_ZOOM.min, TABLE_ZOOM.max) / now;
      if (full && box) free.zoomAt(box.width / 2, box.height / 2, landed);
      else if (full) free.zoomBy(landed);
      else setZoom((z) => clamp(steppedZoom(z, factor), TABLE_ZOOM.min, TABLE_ZOOM.max));
    },
    [full, free],
  );

  return {
    cam,
    full,
    setFull,
    zoomBy,
    reset,
    /** How far the page state scrolls sideways, and where it is. */
    reach: { pan: panned, max: reach, width: frame.width },
    claimsX: claimsSideways(full, panned, bounds, free.camera),
    panTo: (to: number) => setPan(clamp(to, 0, reach)),
    percent: Math.round((full ? free.camera.zoom : zoom) * 100),
    height,
    slot,
    settle,
    keepInView: (at: { x: number; y: number }) => keepInView(at, full ? null : origin, zoom, centre, lift),
  };
}

export type TableState = ReturnType<typeof useTable>;
